/**
 * Scenario Detail Page
 * Full detail view for a single AI Planning Scenario
 * Sprint 4.1.1 - 4.1.4: Detail Page, Diff Visualization, Comments, Approve/Reject
 */

import { useState, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Layout } from '../components/layout';
import { Modal } from '../components/ui/Modal';
import {
  useScenarioDetail,
  useAuditTrail,
  useCalculateScenario,
  useApproveScenario,
  useRejectScenario,
  useApplyScenario,
  useDeleteScenario,
  useAddComment,
  getScenarioStatusInfo,
} from '../hooks/usePlanningScenarios';
import type { ScenarioStatus, AuditTrailEntry } from '../hooks/usePlanningScenarios';
import { useGanttData } from '../hooks/useGanttData';
import { useCapacityHeatmap, useCapacityUtilization } from '../hooks/useCapacityData';
import { useConflicts, useResolveConflict } from '../hooks/useConflicts';
import { useScenarioComparison } from '../hooks/useScenarioComparison';
import { AIActivityTab } from '../components/tabs/AIActivityTab';
import { ResourceTimelineWidget } from '../components/widgets/ResourceTimelineWidget';
import { CapacityHeatmapWidget } from '../components/widgets/CapacityHeatmapWidget';
import { ConflictListWidget } from '../components/widgets/ConflictListWidget';
import { ScenarioComparisonWidget } from '../components/widgets/ScenarioComparisonWidget';
import {
  ArrowLeft,
  Brain,
  Play,
  CheckCircle2,
  XCircle,
  Zap,
  Loader2,
  AlertTriangle,
  Target,
  BarChart3,
  Clock,
  Cpu,
  Coins,
  Calendar,
  FileText,
  Timer,
  Activity,
  GitCompare,
  History,
  RefreshCw,
  RotateCcw,
  Trash2,
  MessageSquare,
  Send,
} from 'lucide-react';
import clsx from 'clsx';

type TabKey = 'overview' | 'timeline' | 'capacity' | 'conflicts' | 'comparison' | 'ai-activity' | 'audit';

const tabs: { key: TabKey; label: string; icon: React.ElementType }[] = [
  { key: 'overview', label: 'Overview', icon: Brain },
  { key: 'timeline', label: 'Timeline', icon: Timer },
  { key: 'capacity', label: 'Capacity', icon: Activity },
  { key: 'conflicts', label: 'Conflicts', icon: AlertTriangle },
  { key: 'comparison', label: 'Comparison', icon: GitCompare },
  { key: 'ai-activity', label: 'AI Activity', icon: Zap },
  { key: 'audit', label: 'Audit Trail', icon: History },
];

function formatDate(dateStr: string) {
  return new Date(dateStr).toLocaleDateString('en-GB', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  });
}

function formatDateTime(dateStr: string) {
  return new Date(dateStr).toLocaleString('en-GB', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
  });
}

function formatProcessingTime(seconds: number | null): string {
  if (seconds == null) return '—';
  if (seconds < 60) return `${seconds.toFixed(1)}s`;
  const mins = Math.floor(seconds / 60);
  const secs = seconds % 60;
  return `${mins}m ${secs.toFixed(0)}s`;
}

function formatTokens(tokens: number | null): string {
  if (tokens == null) return '—';
  if (tokens >= 1000) return `${(tokens / 1000).toFixed(1)}k`;
  return tokens.toString();
}

// ─── Main Component ──────────────────────────────────────────────

export function ScenarioDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState<TabKey>('overview');
  const [groupBy, setGroupBy] = useState<'work_center' | 'order'>('work_center');

  // Modals
  const [showApproveModal, setShowApproveModal] = useState(false);
  const [showRejectModal, setShowRejectModal] = useState(false);
  const [showApplyModal, setShowApplyModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [modalComment, setModalComment] = useState('');

  // Data hooks
  const { data: scenario, isLoading, error, refetch } = useScenarioDetail(id);
  const { data: auditTrail, refetch: refetchAudit } = useAuditTrail(id);
  const { data: ganttData, isLoading: ganttLoading, error: ganttError, refetch: refetchGantt } = useGanttData(id, groupBy);
  const { data: heatmap, isLoading: heatmapLoading, error: heatmapError, refetch: refetchHeatmap } = useCapacityHeatmap(id);
  const { data: utilization, isLoading: utilLoading, error: utilError, refetch: refetchUtil } = useCapacityUtilization(id);
  const { data: conflicts, isLoading: conflictsLoading, error: conflictsError, refetch: refetchConflicts } = useConflicts(id);
  const { data: comparison, isLoading: comparisonLoading, error: comparisonError, refetch: refetchComparison } = useScenarioComparison(id);

  // Mutations
  const calculateMutation = useCalculateScenario();
  const approveMutation = useApproveScenario();
  const rejectMutation = useRejectScenario();
  const applyMutation = useApplyScenario();
  const resolveConflictMutation = useResolveConflict();
  const deleteMutation = useDeleteScenario();
  const addCommentMutation = useAddComment();

  const isAnyMutating = calculateMutation.isPending || approveMutation.isPending || rejectMutation.isPending || applyMutation.isPending;

  // Handlers
  const handleRefreshAll = () => {
    refetch();
    refetchAudit();
    if (activeTab === 'timeline') refetchGantt();
    if (activeTab === 'capacity') { refetchHeatmap(); refetchUtil(); }
    if (activeTab === 'conflicts') refetchConflicts();
    if (activeTab === 'comparison') refetchComparison();
  };

  const handleCalculate = async () => {
    if (!id) return;
    try {
      await calculateMutation.mutateAsync(id);
    } catch (e) {
      console.error('Calculate failed:', e);
    }
  };

  const handleApprove = async () => {
    if (!id) return;
    try {
      await approveMutation.mutateAsync({ id, reason: modalComment || undefined });
      setShowApproveModal(false);
      setModalComment('');
    } catch (e) {
      console.error('Approve failed:', e);
    }
  };

  const handleReject = async () => {
    if (!id) return;
    try {
      await rejectMutation.mutateAsync({ id, reason: modalComment || undefined });
      setShowRejectModal(false);
      setModalComment('');
    } catch (e) {
      console.error('Reject failed:', e);
    }
  };

  const handleApply = async () => {
    if (!id) return;
    try {
      await applyMutation.mutateAsync(id);
      setShowApplyModal(false);
    } catch (e) {
      console.error('Apply failed:', e);
    }
  };

  const handleResolveConflict = async (conflictId: string, note: string) => {
    await resolveConflictMutation.mutateAsync({ conflictId, resolutionNote: note });
    refetchConflicts();
  };

  const handleDelete = useCallback(async () => {
    if (!id) return;
    try {
      await deleteMutation.mutateAsync(id);
      navigate('/planning');
    } catch (e) {
      console.error('Delete failed:', e);
    }
  }, [id, deleteMutation, navigate]);

  // Can delete in all states except 'calculating' and 'applied'
  const canDelete = scenario ? !['calculating', 'applied'].includes(scenario.status) : false;

  // ─── Loading / Error States ──────────────────────────

  if (isLoading) {
    return (
      <Layout title="Loading Scenario...">
        <div className="flex items-center justify-center h-96">
          <Loader2 className="w-8 h-8 text-[#45bdd6] animate-spin" />
          <span className="ml-3 text-gray-500">Loading scenario...</span>
        </div>
      </Layout>
    );
  }

  if (error || !scenario) {
    return (
      <Layout title="Error">
        <div className="flex flex-col items-center justify-center h-96 text-gray-400">
          <AlertTriangle className="w-12 h-12 mb-3" />
          <p className="text-lg font-medium">Scenario not found</p>
          <p className="text-sm mt-1">{error?.message || 'The requested scenario does not exist.'}</p>
          <button
            onClick={() => navigate('/planning')}
            className="mt-4 flex items-center gap-2 px-4 py-2 text-sm text-[#45bdd6] hover:bg-gray-50 rounded-lg"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to Scenarios
          </button>
        </div>
      </Layout>
    );
  }

  const statusInfo = getScenarioStatusInfo(scenario.status);

  // ─── Render ──────────────────────────────────────────

  return (
    <Layout title={scenario.name} onRefresh={handleRefreshAll}>
      <div className="p-6 space-y-5">

        {/* ─── Back Button + Header ─────────────────────── */}
        <button
          onClick={() => navigate('/planning')}
          className="flex items-center gap-1.5 text-sm text-gray-500 hover:text-[#45bdd6] transition-colors"
        >
          <ArrowLeft className="w-4 h-4" />
          Back to Scenarios
        </button>

        <div className="bg-white rounded-xl border border-gray-200 p-6">
          <div className="flex items-start justify-between">
            {/* Left: Name + Status + Period */}
            <div className="flex items-start gap-4">
              <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-[#45bdd6] to-[#674EA7] flex items-center justify-center flex-shrink-0">
                <Brain className="w-6 h-6 text-white" />
              </div>
              <div>
                <div className="flex items-center gap-3">
                  <h1 className="text-xl font-bold text-gray-900">{scenario.name}</h1>
                  <span className={clsx('inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-medium', statusInfo.bgColor, statusInfo.color)}>
                    {scenario.status === 'calculating' && <Loader2 className="w-3 h-3 animate-spin" />}
                    {statusInfo.label}
                  </span>
                </div>
                {scenario.description && (
                  <p className="text-sm text-gray-500 mt-1 max-w-xl">{scenario.description}</p>
                )}
                <div className="flex items-center gap-4 mt-2 text-xs text-gray-400">
                  <span className="flex items-center gap-1">
                    <Calendar className="w-3.5 h-3.5" />
                    {formatDate(scenario.planning_start)} — {formatDate(scenario.planning_end)}
                  </span>
                  {scenario.created_by && (
                    <span>Created by {scenario.created_by}</span>
                  )}
                  <span>Updated {formatDateTime(scenario.updated_at)}</span>
                </div>
              </div>
            </div>

            {/* Right: Action Buttons */}
            <div className="flex items-center gap-2 flex-shrink-0">
              {renderActionButtons(scenario.status, isAnyMutating, handleCalculate, () => setShowApproveModal(true), () => setShowRejectModal(true), () => setShowApplyModal(true))}
              {canDelete && (
                <button
                  onClick={() => setShowDeleteModal(true)}
                  disabled={isAnyMutating || deleteMutation.isPending}
                  className="flex items-center gap-2 px-3 py-2 text-sm text-red-600 bg-white border border-red-200 rounded-lg hover:bg-red-50 disabled:opacity-50 transition-colors"
                  title="Delete Scenario"
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              )}
            </div>
          </div>

          {/* ─── Metrics Row ───────────────────────── */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-5 pt-5 border-t border-gray-100">
            <MetricCard
              icon={Target}
              label="On-Time Delivery"
              value={scenario.on_time_rate != null ? `${Number(scenario.on_time_rate).toFixed(0)}%` : '—'}
              color={scenario.on_time_rate != null ? (Number(scenario.on_time_rate) >= 90 ? 'text-green-600' : Number(scenario.on_time_rate) >= 70 ? 'text-amber-600' : 'text-red-600') : 'text-gray-300'}
            />
            <MetricCard
              icon={BarChart3}
              label="Capacity Utilization"
              value={scenario.capacity_utilization != null ? `${Number(scenario.capacity_utilization).toFixed(0)}%` : '—'}
              color={scenario.capacity_utilization != null ? 'text-blue-600' : 'text-gray-300'}
            />
            <MetricCard
              icon={FileText}
              label="Operations"
              value={String(scenario.total_operations || scenario.planned_operation_count || 0)}
              color="text-gray-700"
            />
            <MetricCard
              icon={AlertTriangle}
              label="Conflicts"
              value={String(scenario.total_conflicts || scenario.conflict_count || 0)}
              color={(scenario.total_conflicts || 0) > 0 ? 'text-red-600' : 'text-green-600'}
            />
          </div>

          {/* ─── AI Info Bar ───────────────────────── */}
          {scenario.ai_model && (
            <div className="flex items-center gap-6 mt-4 pt-4 border-t border-gray-100 text-xs text-gray-400">
              <span className="flex items-center gap-1.5">
                <Cpu className="w-3.5 h-3.5" />
                {scenario.ai_model}
              </span>
              <span className="flex items-center gap-1.5">
                <Coins className="w-3.5 h-3.5" />
                {formatTokens(scenario.ai_prompt_tokens)} prompt / {formatTokens(scenario.ai_completion_tokens)} completion
              </span>
              <span className="flex items-center gap-1.5">
                <Clock className="w-3.5 h-3.5" />
                {formatProcessingTime(scenario.ai_processing_time)}
              </span>
            </div>
          )}
        </div>

        {/* ─── Tab Navigation ──────────────────────── */}
        <div className="border-b border-gray-200">
          <nav className="flex gap-0 -mb-px">
            {tabs.map((tab) => (
              <button
                key={tab.key}
                onClick={() => setActiveTab(tab.key)}
                className={clsx(
                  'flex items-center gap-2 px-5 py-3 text-sm font-medium border-b-2 transition-colors',
                  activeTab === tab.key
                    ? 'border-[#45bdd6] text-[#45bdd6]'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                )}
              >
                <tab.icon className="w-4 h-4" />
                {tab.label}
                {tab.key === 'conflicts' && (scenario.total_conflicts || 0) > 0 && (
                  <span className="ml-1 px-1.5 py-0.5 text-xs font-medium rounded-full bg-red-100 text-red-600">
                    {scenario.total_conflicts}
                  </span>
                )}
              </button>
            ))}
          </nav>
        </div>

        {/* ─── Tab Content ─────────────────────────── */}
        <div className="min-h-[400px]">
          {activeTab === 'overview' && (
            <OverviewTab
              scenario={scenario}
              auditTrail={auditTrail}
              scenarioId={id}
              onAddComment={addCommentMutation}
              onCommentAdded={refetchAudit}
            />
          )}

          {activeTab === 'timeline' && (
            <div className="space-y-3">
              <div className="flex items-center gap-2">
                <span className="text-sm text-gray-500">Group by:</span>
                <select
                  value={groupBy}
                  onChange={(e) => setGroupBy(e.target.value as 'work_center' | 'order')}
                  className="text-sm border border-gray-200 rounded-lg px-3 py-1.5 bg-white"
                >
                  <option value="work_center">Work Center</option>
                  <option value="order">Production Order</option>
                </select>
              </div>
              <ResourceTimelineWidget
                data={ganttData}
                loading={ganttLoading}
                error={ganttError}
                onRefresh={refetchGantt}
                scenarioId={id}
              />
            </div>
          )}

          {activeTab === 'capacity' && (
            <CapacityHeatmapWidget
              heatmap={heatmap}
              utilization={utilization}
              loading={heatmapLoading || utilLoading}
              error={heatmapError || utilError}
              onRefresh={() => { refetchHeatmap(); refetchUtil(); }}
              scenarioId={id}
            />
          )}

          {activeTab === 'conflicts' && (
            <ConflictListWidget
              conflicts={conflicts}
              loading={conflictsLoading}
              error={conflictsError}
              onRefresh={refetchConflicts}
              onResolve={handleResolveConflict}
              scenarioId={id}
              scenarioName={scenario.name}
              isResolving={resolveConflictMutation.isPending}
            />
          )}

          {activeTab === 'comparison' && (
            <ScenarioComparisonWidget
              data={comparison}
              loading={comparisonLoading}
              error={comparisonError}
              onRefresh={refetchComparison}
              scenarioId={id}
            />
          )}

          {activeTab === 'ai-activity' && id && (
            <AIActivityTab scenarioId={id} onRefresh={handleRefreshAll} />
          )}

          {activeTab === 'audit' && (
            <AuditTrailTab entries={auditTrail || []} onRefresh={refetchAudit} />
          )}
        </div>
      </div>

      {/* ─── Modals ────────────────────────────────── */}

      {/* Approve Modal */}
      <Modal isOpen={showApproveModal} onClose={() => { setShowApproveModal(false); setModalComment(''); }} title="Approve Scenario" size="md">
        <div className="space-y-4">
          <p className="text-sm text-gray-600">
            Approve <strong>{scenario.name}</strong> for production write-back. This confirms that the AI-generated plan is acceptable.
          </p>
          <div className="grid grid-cols-2 gap-3 text-sm">
            <div className="bg-gray-50 rounded-lg p-3">
              <span className="text-gray-500">Operations</span>
              <p className="font-semibold">{scenario.total_operations || 0}</p>
            </div>
            <div className="bg-gray-50 rounded-lg p-3">
              <span className="text-gray-500">Conflicts</span>
              <p className={clsx('font-semibold', (scenario.total_conflicts || 0) > 0 ? 'text-red-600' : 'text-green-600')}>
                {scenario.total_conflicts || 0}
              </p>
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Review Comment (optional)</label>
            <textarea
              value={modalComment}
              onChange={(e) => setModalComment(e.target.value)}
              className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-[#45bdd6] focus:border-transparent outline-none resize-none"
              rows={3}
              placeholder="Add a review comment..."
            />
          </div>
          <div className="flex justify-end gap-3 pt-2">
            <button
              onClick={() => { setShowApproveModal(false); setModalComment(''); }}
              className="px-4 py-2 text-sm text-gray-600 bg-white border border-gray-200 rounded-lg hover:bg-gray-50"
            >
              Cancel
            </button>
            <button
              onClick={handleApprove}
              disabled={approveMutation.isPending}
              className="flex items-center gap-2 px-4 py-2 text-sm text-white bg-green-600 rounded-lg hover:bg-green-700 disabled:opacity-50"
            >
              {approveMutation.isPending ? <Loader2 className="w-4 h-4 animate-spin" /> : <CheckCircle2 className="w-4 h-4" />}
              Approve
            </button>
          </div>
          {approveMutation.isError && (
            <p className="text-sm text-red-600 mt-2">Failed to approve: {(approveMutation.error as Error).message}</p>
          )}
        </div>
      </Modal>

      {/* Reject Modal */}
      <Modal isOpen={showRejectModal} onClose={() => { setShowRejectModal(false); setModalComment(''); }} title="Reject Scenario" size="md">
        <div className="space-y-4">
          <p className="text-sm text-gray-600">
            Reject <strong>{scenario.name}</strong>. The scenario will be marked as rejected and can be recalculated with adjustments.
          </p>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Rejection Reason <span className="text-red-500">*</span>
            </label>
            <textarea
              value={modalComment}
              onChange={(e) => setModalComment(e.target.value)}
              className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-red-400 focus:border-transparent outline-none resize-none"
              rows={3}
              placeholder="Explain why this scenario is being rejected..."
            />
          </div>
          <div className="flex justify-end gap-3 pt-2">
            <button
              onClick={() => { setShowRejectModal(false); setModalComment(''); }}
              className="px-4 py-2 text-sm text-gray-600 bg-white border border-gray-200 rounded-lg hover:bg-gray-50"
            >
              Cancel
            </button>
            <button
              onClick={handleReject}
              disabled={rejectMutation.isPending || !modalComment.trim()}
              className="flex items-center gap-2 px-4 py-2 text-sm text-white bg-red-600 rounded-lg hover:bg-red-700 disabled:opacity-50"
            >
              {rejectMutation.isPending ? <Loader2 className="w-4 h-4 animate-spin" /> : <XCircle className="w-4 h-4" />}
              Reject
            </button>
          </div>
          {rejectMutation.isError && (
            <p className="text-sm text-red-600 mt-2">Failed to reject: {(rejectMutation.error as Error).message}</p>
          )}
        </div>
      </Modal>

      {/* Apply Modal */}
      <Modal isOpen={showApplyModal} onClose={() => setShowApplyModal(false)} title="Apply to ERP" size="md">
        <div className="space-y-4">
          <div className="flex items-start gap-3 p-3 bg-amber-50 border border-amber-200 rounded-lg">
            <AlertTriangle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
            <div className="text-sm text-amber-700">
              <p className="font-medium">Production Write-Back</p>
              <p className="mt-1">
                This will write all approved operations back to the ERP system. Operation dates and work center assignments will be updated in the ERP system.
              </p>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3 text-sm">
            <div className="bg-gray-50 rounded-lg p-3">
              <span className="text-gray-500">Operations to write</span>
              <p className="font-semibold">{scenario.total_operations || 0}</p>
            </div>
            <div className="bg-gray-50 rounded-lg p-3">
              <span className="text-gray-500">Planning Period</span>
              <p className="font-semibold text-xs">{formatDate(scenario.planning_start)} — {formatDate(scenario.planning_end)}</p>
            </div>
          </div>
          <div className="flex justify-end gap-3 pt-2">
            <button
              onClick={() => setShowApplyModal(false)}
              className="px-4 py-2 text-sm text-gray-600 bg-white border border-gray-200 rounded-lg hover:bg-gray-50"
            >
              Cancel
            </button>
            <button
              onClick={handleApply}
              disabled={applyMutation.isPending}
              className="flex items-center gap-2 px-4 py-2 text-sm text-white bg-purple-600 rounded-lg hover:bg-purple-700 disabled:opacity-50"
            >
              {applyMutation.isPending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Zap className="w-4 h-4" />}
              Apply to ERP
            </button>
          </div>
          {applyMutation.isError && (
            <p className="text-sm text-red-600 mt-2">Failed to apply: {(applyMutation.error as Error).message}</p>
          )}
        </div>
      </Modal>

      {/* ─── Delete Confirmation Modal ─────────────────── */}
      <Modal isOpen={showDeleteModal} onClose={() => setShowDeleteModal(false)} title="Delete Scenario" size="md">
        <div className="space-y-4">
          <div className="flex items-start gap-3 p-3 bg-red-50 border border-red-200 rounded-lg">
            <Trash2 className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
            <div className="text-sm text-red-700">
              <p className="font-medium">Permanently delete this scenario?</p>
              <p className="mt-1">
                This will permanently delete <strong>{scenario.name}</strong> including all planned operations, conflicts, and audit trail entries. This action cannot be undone.
              </p>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3 text-sm">
            <div className="bg-gray-50 rounded-lg p-3">
              <span className="text-gray-500">Status</span>
              <p className="font-semibold">{statusInfo.label}</p>
            </div>
            <div className="bg-gray-50 rounded-lg p-3">
              <span className="text-gray-500">Operations</span>
              <p className="font-semibold">{scenario.total_operations || 0}</p>
            </div>
          </div>
          <div className="flex justify-end gap-3 pt-2">
            <button
              onClick={() => setShowDeleteModal(false)}
              className="px-4 py-2 text-sm text-gray-600 bg-white border border-gray-200 rounded-lg hover:bg-gray-50"
            >
              Cancel
            </button>
            <button
              onClick={handleDelete}
              disabled={deleteMutation.isPending}
              className="flex items-center gap-2 px-4 py-2 text-sm text-white bg-red-600 rounded-lg hover:bg-red-700 disabled:opacity-50"
            >
              {deleteMutation.isPending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Trash2 className="w-4 h-4" />}
              Delete Scenario
            </button>
          </div>
          {deleteMutation.isError && (
            <p className="text-sm text-red-600 mt-2">Failed to delete: {(deleteMutation.error as Error).message}</p>
          )}
        </div>
      </Modal>
    </Layout>
  );
}

// ─── Sub-Components ──────────────────────────────────────────────

function MetricCard({ icon: Icon, label, value, color }: { icon: React.ElementType; label: string; value: string; color: string }) {
  return (
    <div className="flex items-center gap-3 bg-gray-50 rounded-lg p-3">
      <Icon className={clsx('w-5 h-5 flex-shrink-0', color)} />
      <div>
        <p className={clsx('text-lg font-bold', color)}>{value}</p>
        <p className="text-xs text-gray-500">{label}</p>
      </div>
    </div>
  );
}

function renderActionButtons(
  status: ScenarioStatus,
  disabled: boolean,
  onCalculate: () => void,
  onApprove: () => void,
  onReject: () => void,
  onApply: () => void,
) {
  switch (status) {
    case 'draft':
      return (
        <button onClick={onCalculate} disabled={disabled} className="flex items-center gap-2 px-4 py-2 text-sm text-white bg-blue-600 rounded-lg hover:bg-blue-700 disabled:opacity-50 transition-colors">
          {disabled ? <Loader2 className="w-4 h-4 animate-spin" /> : <Play className="w-4 h-4" />}
          Calculate
        </button>
      );
    case 'calculating':
      return (
        <span className="flex items-center gap-2 px-4 py-2 text-sm text-blue-600 bg-blue-50 rounded-lg">
          <Loader2 className="w-4 h-4 animate-spin" />
          AI is calculating...
        </span>
      );
    case 'pending_review':
      return (
        <>
          <button onClick={onApprove} disabled={disabled} className="flex items-center gap-2 px-4 py-2 text-sm text-white bg-green-600 rounded-lg hover:bg-green-700 disabled:opacity-50 transition-colors">
            <CheckCircle2 className="w-4 h-4" />
            Approve
          </button>
          <button onClick={onReject} disabled={disabled} className="flex items-center gap-2 px-4 py-2 text-sm text-white bg-red-600 rounded-lg hover:bg-red-700 disabled:opacity-50 transition-colors">
            <XCircle className="w-4 h-4" />
            Reject
          </button>
        </>
      );
    case 'approved':
      return (
        <button onClick={onApply} disabled={disabled} className="flex items-center gap-2 px-4 py-2 text-sm text-white bg-purple-600 rounded-lg hover:bg-purple-700 disabled:opacity-50 transition-colors">
          {disabled ? <Loader2 className="w-4 h-4 animate-spin" /> : <Zap className="w-4 h-4" />}
          Apply to ERP
        </button>
      );
    case 'rejected':
    case 'failed':
      return (
        <button onClick={onCalculate} disabled={disabled} className="flex items-center gap-2 px-4 py-2 text-sm text-white bg-blue-600 rounded-lg hover:bg-blue-700 disabled:opacity-50 transition-colors">
          {disabled ? <Loader2 className="w-4 h-4 animate-spin" /> : <RotateCcw className="w-4 h-4" />}
          Re-Calculate
        </button>
      );
    case 'applied':
      return (
        <span className="flex items-center gap-2 px-4 py-2 text-sm text-purple-600 bg-purple-50 rounded-lg">
          <CheckCircle2 className="w-4 h-4" />
          Applied to ERP
        </span>
      );
    default:
      return null;
  }
}

// ─── Overview Tab ──────────────────────────────────────────────

function OverviewTab({
  scenario,
  auditTrail,
  scenarioId,
  onAddComment,
  onCommentAdded,
}: {
  scenario: NonNullable<ReturnType<typeof useScenarioDetail>['data']>;
  auditTrail?: AuditTrailEntry[];
  scenarioId?: string;
  onAddComment: ReturnType<typeof useAddComment>;
  onCommentAdded: () => void;
}) {
  const recentActions = auditTrail?.slice(0, 5) || [];
  const [commentText, setCommentText] = useState('');

  // Filter comments from audit trail
  const comments = (auditTrail || [])
    .filter((entry) => entry.action === 'commented')
    .sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime());

  const handleSubmitComment = async () => {
    if (!scenarioId || !commentText.trim()) return;
    try {
      await onAddComment.mutateAsync({ scenarioId, comment: commentText.trim() });
      setCommentText('');
      onCommentAdded();
    } catch (e) {
      console.error('Failed to add comment:', e);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) {
      e.preventDefault();
      handleSubmitComment();
    }
  };

  return (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
      {/* Left: Scenario Details */}
      <div className="lg:col-span-2 space-y-6">
        {/* Description */}
        {scenario.description && (
          <div className="bg-white rounded-xl border border-gray-200 p-5">
            <h3 className="text-sm font-semibold text-gray-700 mb-2">Description</h3>
            <p className="text-sm text-gray-600 whitespace-pre-wrap">{scenario.description}</p>
          </div>
        )}

        {/* Planning Summary */}
        <div className="bg-white rounded-xl border border-gray-200 p-5">
          <h3 className="text-sm font-semibold text-gray-700 mb-4">Planning Summary</h3>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <InfoItem label="Planning Start" value={formatDate(scenario.planning_start)} />
            <InfoItem label="Planning End" value={formatDate(scenario.planning_end)} />
            <InfoItem label="Production Orders" value={String(scenario.production_order_count || '—')} />
            <InfoItem label="Planned Operations" value={String(scenario.planned_operation_count || scenario.total_operations || 0)} />
          </div>
        </div>

        {/* AI Execution Details */}
        {scenario.ai_model && (
          <div className="bg-white rounded-xl border border-gray-200 p-5">
            <h3 className="text-sm font-semibold text-gray-700 mb-4">AI Execution Details</h3>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <InfoItem label="Model" value={scenario.ai_model} />
              <InfoItem label="Processing Time" value={formatProcessingTime(scenario.ai_processing_time)} />
              <InfoItem label="Prompt Tokens" value={formatTokens(scenario.ai_prompt_tokens)} />
              <InfoItem label="Completion Tokens" value={formatTokens(scenario.ai_completion_tokens)} />
            </div>
          </div>
        )}

        {/* Notes & Comments (Sprint 4.1.3) */}
        <div className="bg-white rounded-xl border border-gray-200 p-5">
          <div className="flex items-center gap-2 mb-4">
            <MessageSquare className="w-4 h-4 text-gray-500" />
            <h3 className="text-sm font-semibold text-gray-700">Notes & Comments</h3>
            {comments.length > 0 && (
              <span className="ml-auto text-xs text-gray-400">{comments.length} comment{comments.length !== 1 ? 's' : ''}</span>
            )}
          </div>

          {/* Comment Input */}
          <div className="flex gap-3 mb-5">
            <div className="flex-1">
              <textarea
                value={commentText}
                onChange={(e) => setCommentText(e.target.value)}
                onKeyDown={handleKeyDown}
                className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-[#45bdd6] focus:border-transparent outline-none resize-none placeholder-gray-400"
                rows={2}
                placeholder="Add a note or comment... (Ctrl+Enter to submit)"
              />
            </div>
            <button
              onClick={handleSubmitComment}
              disabled={!commentText.trim() || onAddComment.isPending}
              className="self-end flex items-center gap-1.5 px-4 py-2 text-sm text-white bg-[#45bdd6] rounded-lg hover:bg-[#3aa8c1] disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              {onAddComment.isPending ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <Send className="w-4 h-4" />
              )}
              Post
            </button>
          </div>
          {onAddComment.isError && (
            <p className="text-xs text-red-600 mb-3">
              Failed to add comment: {(onAddComment.error as Error).message}
            </p>
          )}

          {/* Comment List */}
          {comments.length === 0 ? (
            <div className="text-center py-6 text-gray-400">
              <MessageSquare className="w-8 h-8 mx-auto mb-2 opacity-40" />
              <p className="text-sm">No comments yet. Add the first note!</p>
            </div>
          ) : (
            <div className="space-y-3 max-h-80 overflow-y-auto">
              {comments.map((entry) => (
                <div key={entry.id} className="flex gap-3 p-3 bg-gray-50 rounded-lg">
                  <div className="w-8 h-8 rounded-full bg-[#45bdd6]/10 flex items-center justify-center flex-shrink-0">
                    <span className="text-xs font-semibold text-[#45bdd6] uppercase">
                      {(entry.performed_by || '?')[0]}
                    </span>
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2">
                      <span className="text-sm font-medium text-gray-700">{entry.performed_by}</span>
                      <span className="text-xs text-gray-400">{formatDateTime(entry.created_at)}</span>
                    </div>
                    <p className="text-sm text-gray-600 mt-1 whitespace-pre-wrap break-words">{entry.comment}</p>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Right: Recent Activity + Review Info */}
      <div className="space-y-6">
        <div className="bg-white rounded-xl border border-gray-200 p-5">
          <h3 className="text-sm font-semibold text-gray-700 mb-4">Recent Activity</h3>
          {recentActions.length === 0 ? (
            <p className="text-sm text-gray-400">No activity yet.</p>
          ) : (
            <div className="space-y-3">
              {recentActions.map((entry) => (
                <div key={entry.id} className="flex items-start gap-3">
                  <div className="w-2 h-2 rounded-full bg-[#45bdd6] mt-1.5 flex-shrink-0" />
                  <div className="min-w-0">
                    <p className="text-sm font-medium text-gray-700">{entry.action_display}</p>
                    <p className="text-xs text-gray-400">{entry.performed_by} — {formatDateTime(entry.created_at)}</p>
                    {entry.comment && (
                      <p className="text-xs text-gray-500 mt-0.5 truncate">{entry.comment}</p>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Review Info */}
        <div className="bg-white rounded-xl border border-gray-200 p-5">
          <h3 className="text-sm font-semibold text-gray-700 mb-4">Review Info</h3>
          <div className="space-y-3 text-sm">
            <InfoItem label="Created" value={formatDateTime(scenario.created_at)} />
            <InfoItem label="Last Updated" value={formatDateTime(scenario.updated_at)} />
          </div>
        </div>
      </div>
    </div>
  );
}

function InfoItem({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <p className="text-xs text-gray-400">{label}</p>
      <p className="text-sm font-medium text-gray-700 mt-0.5">{value}</p>
    </div>
  );
}

// ─── Audit Trail Tab ──────────────────────────────────────────

function AuditTrailTab({ entries, onRefresh }: { entries: AuditTrailEntry[]; onRefresh: () => void }) {
  const sorted = [...entries].sort(
    (a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
  );

  const getActionBadge = (action: string) => {
    const actionMap: Record<string, { color: string; bg: string }> = {
      created: { color: 'text-gray-600', bg: 'bg-gray-100' },
      calculated: { color: 'text-blue-600', bg: 'bg-blue-100' },
      calculation_completed: { color: 'text-blue-600', bg: 'bg-blue-100' },
      calculation_failed: { color: 'text-red-600', bg: 'bg-red-100' },
      approved: { color: 'text-green-600', bg: 'bg-green-100' },
      rejected: { color: 'text-red-600', bg: 'bg-red-100' },
      applied: { color: 'text-purple-600', bg: 'bg-purple-100' },
      apply_failed: { color: 'text-red-600', bg: 'bg-red-100' },
      writeback_completed: { color: 'text-purple-600', bg: 'bg-purple-100' },
      conflict_resolved: { color: 'text-green-600', bg: 'bg-green-100' },
      operations_approved: { color: 'text-green-600', bg: 'bg-green-100' },
      operations_rejected: { color: 'text-red-600', bg: 'bg-red-100' },
      commented: { color: 'text-gray-600', bg: 'bg-gray-100' },
    };
    return actionMap[action] || { color: 'text-gray-600', bg: 'bg-gray-100' };
  };

  return (
    <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
      <div className="flex items-center justify-between px-5 py-3 border-b border-gray-100">
        <h3 className="text-sm font-semibold text-gray-700">Audit Trail</h3>
        <button
          onClick={onRefresh}
          className="p-1.5 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
        >
          <RefreshCw className="w-4 h-4" />
        </button>
      </div>

      {sorted.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-12 text-gray-400">
          <History className="w-10 h-10 mb-2 opacity-50" />
          <p className="text-sm">No audit trail entries yet.</p>
        </div>
      ) : (
        <table className="w-full">
          <thead>
            <tr className="bg-gray-50">
              <th className="text-left text-xs font-semibold text-gray-500 uppercase tracking-wider px-5 py-2.5">Timestamp</th>
              <th className="text-left text-xs font-semibold text-gray-500 uppercase tracking-wider px-4 py-2.5">Action</th>
              <th className="text-left text-xs font-semibold text-gray-500 uppercase tracking-wider px-4 py-2.5">User</th>
              <th className="text-left text-xs font-semibold text-gray-500 uppercase tracking-wider px-4 py-2.5">Status Change</th>
              <th className="text-left text-xs font-semibold text-gray-500 uppercase tracking-wider px-4 py-2.5">Comment</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {sorted.map((entry) => {
              const badge = getActionBadge(entry.action);
              return (
                <tr key={entry.id} className="hover:bg-gray-50">
                  <td className="px-5 py-3 text-xs text-gray-500 whitespace-nowrap">{formatDateTime(entry.created_at)}</td>
                  <td className="px-4 py-3">
                    <span className={clsx('inline-flex px-2.5 py-0.5 rounded-full text-xs font-medium', badge.bg, badge.color)}>
                      {entry.action_display}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-sm text-gray-600">{entry.performed_by || '—'}</td>
                  <td className="px-4 py-3 text-xs text-gray-500">
                    {entry.old_status && entry.new_status ? (
                      <span>{entry.old_status} → {entry.new_status}</span>
                    ) : '—'}
                  </td>
                  <td className="px-4 py-3 text-sm text-gray-500 max-w-xs truncate">{entry.comment || '—'}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      )}
    </div>
  );
}
